/**
 * @file OrderCreatePage.tsx
 * @description Создание заказа менеджером (phase19.24 шаг 5.1, проект
 * `phase19.24-step5-1-receipt-method-design.md` §2).
 *
 * Страница — раскладка из пяти карточек: клиент → позиции → способ получения
 * → оплата → итого и отправка. Данные и решения держит `useOrderCreateForm`,
 * способ получения рисует общий с согласованием `ReceiptMethodFields`.
 *
 * Сценарий С1 («заказ по звонку»): менеджер собирает состав, выбирает исход и
 * одним действием создаёт заказ СРАЗУ в «Согласовании доставки» — письмо
 * клиенту шлёт сервер. Без e-mail авто-отправка заблокирована: остаётся
 * «Создать и согласовать по телефону» (решение пользователя 2026-09-21 §7 п.3).
 *
 * phase19.31: заголовок и главное действие — в верхней панели
 * (`OrderCreateHeader`), на телефоне кнопка в общей нижней полосе каркаса.
 * Ширина — общая колонка кабинета (SC55): поля формы слева не шире 720px,
 * «Итого и отправка» справа от 1024 и закреплена при прокрутке.
 */

import { Link2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { CustomerPickerDialog } from "../../../components/orders/CustomerPickerDialog";
import { OrderCreateCustomerCard } from "../../../components/orders/OrderCreateCustomerCard";
import { OrderCreateHeader } from "../../../components/orders/OrderCreateHeader";
import { OrderCreateItemsCard } from "../../../components/orders/OrderCreateItemsCard";
import { OrderCreateSubmitCard } from "../../../components/orders/OrderCreateSubmitCard";
import { OrderPaymentTermsField } from "../../../components/orders/OrderPaymentTermsField";
import { ReceiptMethodFields } from "../../../components/orders/ReceiptMethodFields";
import { SkuPickerDialog } from "../../../components/orders/SkuPickerDialog";
import { Alert } from "../../../components/ui/alert";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { Label } from "../../../components/ui/label";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { Textarea } from "../../../components/ui/textarea";
import { useOrderCreateForm, type OrderCreateSubmitMode } from "../../../hooks/useOrderCreateForm";
import { RECEIPT_METHOD_META } from "../../../lib/receipt-method";

export function OrderCreatePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  // Создание заказа — право менеджера: наблюдателю форма видна, действия нет.
  const canManage = useHasPermission("SHOP_MANAGE_ORDERS");
  const [searchParams] = useSearchParams();
  // Заход из карточки сделки: клиент и позиции подставятся, а сама сделка
  // после сохранения уйдёт в стадию «Сделка успешна» (phase10.5).
  const dealId = searchParams.get("dealId");
  const form = useOrderCreateForm({
    dealId,
    preCustomerId: searchParams.get("customerId"),
  });
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [skuPickerOpen, setSkuPickerOpen] = useState(false);

  const customer = form.customer.customer;
  const method = form.receipt.method;
  // Письмо уходит только когда есть кому и куда: адрес пункта и e-mail.
  const blockedReason = !customer
    ? "Выберите клиента — письмо отправлять некому."
    : !customer.email
      ? "У клиента нет e-mail: добавьте адрес или согласуйте по телефону."
      : method === "PICKUP" && !form.pickup.ready && !form.pickup.loading
        ? "Пункт самовывоза не заполнен — клиент не узнает, куда ехать."
        : null;

  async function run(mode: OrderCreateSubmitMode): Promise<void> {
    const order = await form.submit(mode);
    if (!order) return;
    notify.success(`Заказ ${order.number} создан`);
    // Письмо не ушло, а заказ есть — говорим об этом сразу: иначе менеджер
    // ждёт ответа клиента, которому ничего не отправляли (§2 «Состояния»).
    if (mode === "send" && order.stage?.kind !== "PENDING_DELIVERY_AGREEMENT") {
      notify.warning("Письмо не отправлено — отправьте повторно из блока «Согласование доставки»");
    }
    if (mode === "phone" && order.agreementLink) {
      notify.info("Ссылку подтверждения продиктуйте клиенту — она в блоке заявки");
    }
    navigate(`/shop/${slug}/orders/${order.id}`);
  }

  return (
    // Место под нижней полосой на телефоне резервирует каркас.
    <div className="flex w-full flex-col gap-4 lg:gap-6">
      <OrderCreateHeader
        canManage={canManage}
        saving={form.saving}
        blockedReason={blockedReason}
        backTo={`/shop/${slug}/orders`}
        onSubmit={(mode) => void run(mode)}
      />

      {dealId ? (
        <NoticeStrip>
          <Link2 className="mr-1 inline-block size-4 align-text-bottom" />
          Заказ создаётся из сделки: после сохранения сделка перейдёт в стадию «Сделка успешна».
        </NoticeStrip>
      ) : null}

      {form.error ? <Alert severity="error">{form.error}</Alert> : null}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,720px)_minmax(280px,360px)] lg:gap-6">
        <div className="flex min-w-0 flex-col gap-4 lg:gap-6">
          <OrderCreateCustomerCard picker={form.customer} onOpenList={() => setCustomerPickerOpen(true)} />

          <OrderCreateItemsCard
            rows={form.rows}
            onAddEmpty={form.addEmptyRow}
            onOpenCatalog={() => setSkuPickerOpen(true)}
            onPickSku={form.pickSku}
            onPatch={form.patchRow}
            onRemove={form.removeRow}
          />

          <EditorialCard>
            <EditorialCard.Body>
              <ReceiptMethodFields
                value={form.receipt}
                onChange={form.setReceipt}
                pickup={form.pickup}
                issue={form.issue}
                disabled={form.saving}
                shopSlug={slug}
                idPrefix="create"
              />
            </EditorialCard.Body>
          </EditorialCard>

          <EditorialCard>
            <EditorialCard.Body>
              <OrderPaymentTermsField
                method={method}
                value={form.paymentTerms}
                onChange={form.setPaymentTerms}
                legalEntity={customer?.type === "LEGAL"}
                disabled={form.saving}
                idPrefix="create"
              />
            </EditorialCard.Body>
          </EditorialCard>

          <EditorialCard>
            <EditorialCard.Body>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="o-notes">Заметки для магазина</Label>
                <Textarea
                  id="o-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => form.setNotes(e.target.value)}
                  placeholder="Доставка завтра до 14:00, позвонить за час"
                />
              </div>
            </EditorialCard.Body>
          </EditorialCard>
        </div>

        {/* Итог справа от 1024 и закреплён: сумма видна, пока правят состав. */}
        <div className="min-w-0 lg:sticky lg:top-4">
          <OrderCreateSubmitCard
            canManage={canManage}
            goodsTotal={form.goodsTotal}
            deliveryAmount={form.deliveryAmount}
            deliveryNote={RECEIPT_METHOD_META[method].deliveryNote}
            customerEmail={customer?.email ?? null}
            blockedReason={blockedReason}
          />
        </div>
      </div>

      {customerPickerOpen ? (
        <CustomerPickerDialog
          onClose={() => setCustomerPickerOpen(false)}
          onSelect={(picked) => {
            form.customer.pick(picked);
            setCustomerPickerOpen(false);
          }}
        />
      ) : null}
      {skuPickerOpen ? (
        <SkuPickerDialog
          onClose={() => setSkuPickerOpen(false)}
          onSelectMany={(skus) => {
            form.addManyFromCatalog(skus);
            setSkuPickerOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
